import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Patient } from "@models/patient";
import OnboardingForm from "@components/Onboarding/OnboardingForm/OnboardingForm";
import {
  OnboardingFormActionContainer,
  OnboardingFormContainer,
} from "@components/Onboarding/OnboardingForm";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { setMessage } from "@store/snackbarSlice";
import CompanionForm from "@components/CompanionForm";
import { CompanionFormType } from "@components/CompanionForm/CompanionForm";
import {
  useGetCompanionsQuery,
  useUpdateCompanionMutation,
} from "@store/companionApi";
import { useGetAvatarsQuery, useGetVoicesQuery } from "@store/avatarApi";
import { Companion as CompanionModel } from "@models/companion";
import { useNavigate } from "react-router-dom";
import { RecordVoiceOver } from "@mui/icons-material";
import { RootState } from "@store/store";
import Loader from "@components/Loader/Loader";
import { useGetAccountsQuery } from "@store/accountApi";

const OnboardingCompanionForm: FC<{
  patient: Patient | null;
  isPatient: boolean;
}> = ({ patient, isPatient = true }) => {
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const { data: accounts } = useGetAccountsQuery(undefined, {
    skip: isPatient,
  });
  const dispatch = useDispatch();
  const { data: companions } = useGetCompanionsQuery(
    { patientId: isPatient ? patient?.id : undefined, userId: userId! },
    {
      skip: !patient?.id && !userId,
    },
  );
  const { data: avatars } = useGetAvatarsQuery();
  const { data: voices } = useGetVoicesQuery();
  const [updateCompanionApi] = useUpdateCompanionMutation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const language = useMemo(() => {
    if (isPatient) {
      return patient ? patient.language : null;
    }
    const account = accounts?.data?.[0];
    return account ? account.language : null;
  }, [isPatient, patient, accounts]);

  const form = useForm<CompanionFormType>();
  const {
    clearErrors,
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form;

  const resetForm = useCallback(
    (comp?: CompanionModel) => {
      reset({
        name: comp?.name,
        avatarId: comp?.avatar?.id,
        voiceId: comp?.voice?.id,
        isDefault: comp?.isDefault,
      });
    },
    [reset],
  );

  useEffect(() => {
    resetForm(companions?.data?.[0]);
  }, [resetForm, companions]);

  const updateCompanion = useCallback(
    async (companion: Partial<CompanionModel> & { id: string }) => {
      const res = await updateCompanionApi(companion);
      if (res.error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        const nextUrl = isPatient
          ? patient
            ? `/onboarding/${patient.id}/companion/caregiver`
            : "/onboarding/companion/caregiver"
          : `/onboarding/billing`;
        navigate(nextUrl);
      }
      return res;
    },
    [dispatch, t, isPatient, patient, updateCompanionApi, navigate],
  );

  const onSave = useCallback(
    async (
      companion: Omit<
        CompanionModel,
        "id" | "patientId" | "avatar" | "voice" | "isDefault"
      >,
    ) => {
      await updateCompanion({
        ...companion,
        id: companions?.data?.[0].id!,
      });
    },
    [companions, updateCompanion],
  );

  const onSubmit = async (formValue: CompanionFormType) => {
    if (!isValid) {
      return;
    }

    const companion = {
      name: formValue.name!,
      avatarId: formValue.avatarId!,
      voiceId: formValue.voiceId!,
      patientId: isPatient ? patient?.id : undefined,
      userId: !isPatient ? userId : undefined,
    };
    await onSave(companion);
  };

  const onPrevious = useCallback(() => {
    const previousUrl = isPatient
      ? patient
        ? `/onboarding/${patient.id}/characters`
        : "/onboarding/characters"
      : patient
        ? `/onboarding/${patient.id}/companion/patient`
        : "/onboarding/companion/patient";
    navigate(previousUrl);
  }, [navigate, isPatient, patient]);

  if (!language) {
    return <Loader />;
  }

  return (
    <OnboardingForm
      title={t(
        `onboardingCompanion.${isPatient ? "patient" : "caregiver"}.title`,
        { person: patient?.firstName },
      )}
      subtitle={t(
        `onboardingCompanion.${isPatient ? "patient" : "caregiver"}.subtitle`,
      )}
      icon={<RecordVoiceOver fontSize="small" />}
    >
      <OnboardingFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(onSubmit)(e);
        }}
      >
        <CompanionForm
          form={form}
          avatars={avatars?.data}
          voices={voices?.data}
          language={language}
        />

        <OnboardingFormActionContainer
          isSubmitting={isSubmitting}
          onPrevious={onPrevious}
        />
      </OnboardingFormContainer>
    </OnboardingForm>
  );
};

export default OnboardingCompanionForm;
